import React from 'react';
import type { AlertItem } from './types';

const TYPE_CONFIG: Record<AlertItem['type'], { background: string; dotColor: string }> = {
  info: { background: '#E6F1FB', dotColor: '#378ADD' },
  warn: { background: '#FAEEDA', dotColor: '#BA7517' },
  ok:   { background: '#E1F5EE', dotColor: '#1D9E75' },
};

interface Props {
  alerts: AlertItem[];
}

export default function AlertsFeed({ alerts }: Props) {
  const isEmpty = !alerts.length;

  return (
    <div style={styles.panel}>
      <p style={styles.title}>Alerts</p>
      <p style={styles.sub}>{isEmpty ? 'No active alerts' : `${alerts.length} alert${alerts.length > 1 ? 's' : ''}`}</p>

      {isEmpty ? (
        <div style={styles.empty}>
          <i className="ti ti-circle-check" aria-hidden="true" style={{ fontSize: 20, color: '#1D9E75' }} />
          <span style={styles.emptyText}>All clear</span>
        </div>
      ) : (
        <ul style={styles.list} aria-label="Operational alerts">
          {alerts.map(alert => {
            const config = TYPE_CONFIG[alert.type];
            return (
              <li key={alert.id} style={{ ...styles.item, background: 'var(--color-bg-primary)' }}>
                <span style={{ ...styles.dot, background: config.dotColor }} aria-hidden="true" />
                <div>
                  <p style={styles.message}>{alert.message}</p>
                  <p style={styles.time}>{alert.time}</p>
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  panel:     { background: 'var(--color-bg-secondary, #f5f5f3)', borderRadius: 12, padding: '1.1rem 1.25rem', fontFamily: "'Syne', sans-serif" },
  title:     { fontSize: 13, fontWeight: 600, color: 'var(--color-text-primary, #2C2C2A)', margin: '0 0 0.15rem' },
  sub:       { fontFamily: "'DM Mono', monospace", fontSize: 11, color: 'var(--color-text-secondary, #5F5E5A)', margin: '0 0 0.85rem' },
  list:      { listStyle: 'none', display: 'flex', flexDirection: 'column', gap: 6, padding: 0, margin: 0 },
  item:      { display: 'flex', alignItems: 'flex-start', gap: 8, padding: '8px 10px', borderRadius: 8 },
  dot:       { width: 6, height: 6, borderRadius: '50%', marginTop: 4, flexShrink: 0 },
  message:   { fontSize: 12, color: 'var(--color-text-primary, #2C2C2A)', lineHeight: 1.4, margin: 0 },
  time:      { fontFamily: "'DM Mono', monospace", fontSize: 10, color: 'var(--color-text-secondary, #5F5E5A)', margin: '2px 0 0' },
  empty:     { display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 6, padding: '1.5rem 0' },
  emptyText: { fontSize: 12, color: 'var(--color-text-secondary, #5F5E5A)', fontFamily: "'DM Mono', monospace" },
};
